import React from 'react';
import { BathFixtureShell, Rect, Line, Circle } from './BathFixtureFrame';
import { KITCHEN_SINK_DRAIN_RADIUS_RATIO } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Kitchen sink variants — dispatched on `obj.subtype`. Canonical values
 * from `shared-lib/sink-types.js` for `type: 'kitchen-sink'`:
 *   - undermount (default): visible basin band; rim is concealed.
 *     Elevation reads as a single inset basin under a thin countertop
 *     edge line.
 *   - drop-in: rim sits above the counter; show a slim rim band on
 *     top + basin below.
 *   - farmhouse / apron-front: full visible apron front spans the
 *     entire vertical extent (the basin is the silhouette).
 *
 * Plus a layout dimension: single- vs. double-basin. There is no
 * canonical field for basin count in the data model today; we infer
 * double-basin from a `basinCount` / `bowlCount` field if present,
 * otherwise fall back to a width threshold (36"+ defaults to double,
 * matching common residential sink stocking).
 */

const INCH_M = 0.0254;
const DOUBLE_BASIN_WIDTH_THRESHOLD_M = 36 * INCH_M;
const VALID_SUBTYPES = new Set([
  'undermount',
  'drop-in',
  'dropin',
  'farmhouse',
  'apron-front',
]);

export function resolveKitchenSinkVariant(obj = {}) {
  const raw = obj.subtype;
  if (raw === 'farmhouse' || raw === 'apron-front') return 'farmhouse';
  if (raw === 'drop-in' || raw === 'dropin') return 'drop-in';
  if (VALID_SUBTYPES.has(raw)) return raw;
  return 'undermount';
}

export function resolveKitchenSinkBasinCount(obj = {}, widthMeters = 0) {
  if (typeof obj.basinCount === 'number') return obj.basinCount;
  if (typeof obj.bowlCount === 'number') return obj.bowlCount;
  return widthMeters >= DOUBLE_BASIN_WIDTH_THRESHOLD_M ? 2 : 1;
}

export function KitchenSinkSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SideView
        view={view}
        width={widthMeters}
        depth={depthMeters}
        height={heightMeters}
        obj={obj}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <FrontView
      width={widthMeters}
      height={heightMeters}
      obj={obj}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

KitchenSinkSchematic.supportsSideViews = true;

function FrontView({ width, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveKitchenSinkVariant(obj);
  const basinCount = resolveKitchenSinkBasinCount(obj, width);
  const props = {
    width,
    height,
    basinCount,
    strokeColor,
    strokeWidth,
  };
  return (
    <BathFixtureShell width={width} height={height}>
      {variant === 'farmhouse' && <Farmhouse {...props} />}
      {variant === 'drop-in' && <DropIn {...props} />}
      {variant === 'undermount' && <Undermount {...props} />}
    </BathFixtureShell>
  );
}

/**
 * Side elevation. Symmetric front-to-back (the basin profile is the
 * same on either end), so we draw once and the R view mirrors the L
 * via the wrapper `<g transform>`. The undermount basin IS the object
 * box in 3D (`kitchenSinkRenderer`: flat walls + floor, open top), so
 * its section is the box itself — rim at the top edge, walls, floor —
 * with the drain on the floor; a curved profile would not match the
 * model. For farmhouse, the apron drops to the floor with no inset.
 */
function SideView({
  view,
  width,
  depth,
  height,
  obj,
  strokeColor,
  strokeWidth,
}) {
  const variant = resolveKitchenSinkVariant(obj);
  // The renderer's own drain rule: a share of min(width, depth).
  const drainR =
    Math.min(...[width, depth].filter(Number.isFinite)) *
    KITCHEN_SINK_DRAIN_RADIUS_RATIO;
  const props = { depth, height, drainR, strokeColor, strokeWidth };
  return (
    <BathFixtureShell width={depth} height={height}>
      <Rect
        x={0}
        y={0}
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <g
        transform={
          view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
        }
      >
        {variant === 'farmhouse' && <FarmhouseSide {...props} />}
        {variant === 'drop-in' && <DropInSide {...props} />}
        {variant === 'undermount' && <UndermountSide {...props} />}
      </g>
    </BathFixtureShell>
  );
}

/** Drain disc on the basin floor, seen in section. */
function SideDrain({ depth, height, drainR, strokeColor, strokeWidth }) {
  return (
    <Circle
      cx={depth / 2}
      cy={height - drainR}
      r={drainR}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/** Undermount: the frame rect IS the basin section; only the drain is added. */
function UndermountSide(props) {
  return <SideDrain {...props} />;
}

/** Drop-in: the same basin with its rim lip resting on the counter. */
function DropInSide({ depth, height, drainR, strokeColor, strokeWidth }) {
  const rimH = Math.min(height * 0.08, 0.03);
  return (
    <g>
      <Line
        x1={0}
        y1={rimH}
        x2={depth}
        y2={rimH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <SideDrain
        depth={depth}
        height={height}
        drainR={drainR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Farmhouse: apron drops below the counter to the floor on the front
 * face. The un-mirrored frame puts the front of the sink at x=depth
 * (right side); the apron at the right edge drops to the bottom. The
 * back of the sink (against the wall) is at x=0.
 */
function FarmhouseSide({ depth, height, strokeColor, strokeWidth }) {
  // Counter / deck line on the back portion only — front is open apron
  const counterY = Math.min(height * 0.08, 0.035);
  const apronStartX = depth * 0.7;
  return (
    <g>
      {/* Back counter line */}
      <Line
        x1={0}
        y1={counterY}
        x2={apronStartX}
        y2={counterY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Apron front face — vertical drop near front edge */}
      <Line
        x1={apronStartX}
        y1={counterY}
        x2={apronStartX}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

function BasinDividers({
  width,
  basinCount,
  y0,
  y1,
  strokeColor,
  strokeWidth,
}) {
  if (basinCount <= 1) return null;
  const lines = [];
  for (let i = 1; i < basinCount; i++) {
    const x = (width * i) / basinCount;
    lines.push(
      <Line
        key={i}
        x1={x}
        y1={y0}
        x2={x}
        y2={y1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return <g>{lines}</g>;
}

/**
 * Drain disc per basin — small circle near the bottom-center of each
 * basin band. Matches the dark drain ring `kitchenSinkRenderer` emits
 * on the basin floor. Sized as ~8% of the smaller bbox dimension so it
 * reads at this scale without dominating the basin.
 */
function BasinDrains({
  basinX0,
  basinW,
  basinCount,
  y0,
  y1,
  strokeColor,
  strokeWidth,
}) {
  const slotW = basinW / basinCount;
  const drainR = Math.min(slotW, y1 - y0) * 0.08;
  const cy = y0 + (y1 - y0) * 0.6;
  return (
    <g>
      {Array.from({ length: basinCount }, (_, i) => (
        <Circle
          key={`drain-${i}`}
          cx={basinX0 + slotW * (i + 0.5)}
          cy={cy}
          r={drainR}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

/**
 * Undermount: thin counter line at the top + inset basin below. Basin
 * inset = 1" on each side, reading as the basin hung under the counter.
 */
function Undermount({ width, height, basinCount, strokeColor, strokeWidth }) {
  const counterH = Math.min(height * 0.08, 0.035);
  const inset = Math.min(width, height) * 0.04;
  const basinX = inset;
  const basinY = counterH;
  const basinW = width - inset * 2;
  const basinH = height - counterH - inset;
  return (
    <g>
      <Line
        x1={0}
        y1={counterH}
        x2={width}
        y2={counterH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={basinX}
        y={basinY}
        width={basinW}
        height={basinH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDividers
        width={basinW + basinX * 0}
        basinCount={basinCount}
        y0={basinY}
        y1={basinY + basinH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDrains
        basinX0={basinX}
        basinW={basinW}
        basinCount={basinCount}
        y0={basinY}
        y1={basinY + basinH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Drop-in: slim rim band on top of the counter + basin below. Rim
 * height ~3/4" — visible as a thin horizontal stripe above the
 * basin opening.
 */
function DropIn({ width, height, basinCount, strokeColor, strokeWidth }) {
  const rimH = Math.min(height * 0.08, 0.03);
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={0}
        y1={rimH}
        x2={width}
        y2={rimH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDividers
        width={width}
        basinCount={basinCount}
        y0={rimH}
        y1={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDrains
        basinX0={0}
        basinW={width}
        basinCount={basinCount}
        y0={rimH}
        y1={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Farmhouse / apron-front: apron is the front face — the basin
 * silhouette IS the full rectangle, no inset, no top rim.
 */
function Farmhouse({ width, height, basinCount, strokeColor, strokeWidth }) {
  return (
    <g>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDividers
        width={width}
        basinCount={basinCount}
        y0={0}
        y1={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <BasinDrains
        basinX0={0}
        basinW={width}
        basinCount={basinCount}
        y0={0}
        y1={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
